import { Modal, notification } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdCheckCircle,
  MdErrorOutline,
  MdTableChart,
  MdUpload,
} from "react-icons/md";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import { REGEX_EXPS } from "utils/constants";

function parseCSVLine(line, delimiter) {
  const result = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
}

export default function OrganizationCSVMappingModal({
  isOpen,
  onClose,
  onImport,
  csvFile,
}) {
  const [headers, setHeaders] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [mapping, setMapping] = useState({
    name: null,
    zipCode: null,
    city: null,
    description: null,
  });
  const [importing, setImporting] = useState(false);
  const [validNames, setValidNames] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.COMPANY);

  const validateMapping = useCallback((currentMapping, lines, delimiter) => {
    if (currentMapping.name === null || currentMapping.zipCode === null) {
      setValidNames(0);
      return;
    }

    let count = 0;
    const zipCodeRegex = new RegExp(REGEX_EXPS.zipCode);
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i], delimiter);
      const name = values[currentMapping.name]?.trim();
      const zipCode = values[currentMapping.zipCode]?.trim();
      if (
        name &&
        name.length > 0 &&
        zipCode &&
        zipCode.length > 0 &&
        zipCodeRegex.test(zipCode)
      ) {
        count++;
      }
    }
    setValidNames(count);
  }, []);

  const parseCSV = useCallback(async () => {
    if (!csvFile) return;

    const fileName = csvFile.name.toLowerCase();
    const isExcel = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

    if (isExcel) {
      notification.error({
        message: "Excel files not supported",
        description:
          "Please convert your Excel file to CSV format before importing.",
      });
      return;
    }

    const text = await csvFile.text();
    const lines = text.split("\n").filter((line) => line.trim());

    if (lines.length === 0) return;

    const delimiter = lines[0].includes(";") ? ";" : ",";
    const parsedHeaders = parseCSVLine(lines[0], delimiter);
    setHeaders(parsedHeaders);

    const preview = lines
      .slice(1, Math.min(6, lines.length))
      .map((line) => parseCSVLine(line, delimiter));
    setPreviewData(preview);
    setTotalRows(lines.length - 1);

    const autoMapping = {
      name: null,
      zipCode: null,
      city: null,
      description: null,
    };

    parsedHeaders.forEach((header, index) => {
      const lower = header.toLowerCase();
      if (
        lower.includes("nom") ||
        lower.includes("name") ||
        lower.includes("collectivité") ||
        lower.includes("collectivite") ||
        lower.includes("organisation") ||
        lower.includes("organization")
      ) {
        autoMapping.name = index;
      } else if (
        lower.includes("code postal") ||
        lower.includes("codepostal") ||
        lower.includes("zip") ||
        lower.includes("cp") ||
        lower.includes("postal")
      ) {
        autoMapping.zipCode = index;
      } else if (
        lower.includes("ville") ||
        lower.includes("city") ||
        lower.includes("département") ||
        lower.includes("departement") ||
        lower.includes("department")
      ) {
        autoMapping.city = index;
      } else if (
        lower.includes("description") ||
        lower.includes("desc") ||
        lower.includes("comment") ||
        lower.includes("commentaire") ||
        lower.includes("note") ||
        lower.includes("notes")
      ) {
        autoMapping.description = index;
      }
    });

    setMapping(autoMapping);
    validateMapping(autoMapping, lines, delimiter);
  }, [csvFile, validateMapping]);

  useEffect(() => {
    if (isOpen && csvFile) {
      parseCSV();
    }
  }, [isOpen, csvFile, parseCSV]);

  function handleMappingChange(field, value) {
    const newMapping = { ...mapping, [field]: value };
    setMapping(newMapping);

    if (csvFile) {
      csvFile.text().then((text) => {
        const lines = text.split("\n").filter((line) => line.trim());
        const delimiter = lines[0].includes(";") ? ";" : ",";
        validateMapping(newMapping, lines, delimiter);
      });
    }
  }

  async function handleImport() {
    if (!csvFile || mapping.name === null || mapping.zipCode === null) return;

    setImporting(true);

    const text = await csvFile.text();
    const lines = text.split("\n").filter((line) => line.trim());
    const delimiter = lines[0].includes(";") ? ";" : ",";

    const mappedData = [];
    const zipCodeRegex = new RegExp(REGEX_EXPS.zipCode);
    const invalidRows = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i], delimiter);
      const name = values[mapping.name]?.trim();
      const zipCode = values[mapping.zipCode]?.trim();

      if (name && name.length > 0 && zipCode && zipCode.length > 0) {
        if (zipCodeRegex.test(zipCode)) {
          mappedData.push({
            name,
            zipCode,
            city:
              mapping.city !== null ? values[mapping.city]?.trim() || "" : "",
            description:
              mapping.description !== null
                ? values[mapping.description]?.trim() || ""
                : "",
          });
        } else {
          invalidRows.push(i + 1);
        }
      }
    }

    if (invalidRows.length > 0) {
      notification.warning({
        message:
          t("CONTRACT_SECTION.CSV_MAPPING_MODAL.INVALID_ZIPCODE_TITLE") ||
          "Invalid zip codes found",
        description:
          t("CONTRACT_SECTION.CSV_MAPPING_MODAL.INVALID_ZIPCODE_DESC", {
            count: invalidRows.length,
            rows: invalidRows.slice(0, 10).join(", "),
          }) ||
          `${invalidRows.length} row(s) with invalid zip codes (rows: ${invalidRows.slice(0, 10).join(", ")})`,
      });
    }

    setImporting(false);
    if (mappedData.length > 0) {
      onImport(mappedData);
      onClose();
    } else {
      notification.error({
        message:
          t("CONTRACT_SECTION.CSV_MAPPING_MODAL.NO_VALID_DATA") ||
          "No valid data",
        description:
          t("CONTRACT_SECTION.CSV_MAPPING_MODAL.NO_VALID_DATA_DESC") ||
          "No valid organizations found. Please check that name and zip code are correctly mapped and zip codes are in valid format (5 digits).",
      });
    }
  }

  const canImport =
    mapping.name !== null && mapping.zipCode !== null && validNames > 0;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      width="80rem"
      centered
      footer={null}
      className="company__csv-mapping-modal"
      title={
        <div className="company__flex company__items-center company__gap-3">
          <div className="company__w-10 company__h-10 company__bg-green-600 company__rounded-lg company__flex company__items-center company__justify-center">
            <MdTableChart className="company__w-5 company__h-5 company__text-white" />
          </div>
          <div>
            <h2 className="company__text-xl company__font-semibold company__text-slate-900 company__m-0">
              {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.HEADER.TITLE") ||
                "Map CSV Columns"}
            </h2>
            <p className="company__text-sm company__text-slate-600 company__m-0">
              {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.HEADER.ROWS", {
                count: totalRows,
              }) || `${totalRows} rows found`}
            </p>
          </div>
        </div>
      }
    >
      <div
        className="company__space-y-6"
        style={{ maxHeight: "calc(90vh - 200px)", overflowY: "auto" }}
      >
        <div className="company__info-box company__p-4 company__flex company__gap-3">
          <MdErrorOutline
            className="company__w-5 company__h-5 company__text-blue-600 company__flex-shrink-0"
            style={{ marginTop: "0.125rem" }}
          />
          <div className="company__text-sm company__text-blue-900">
            <p className="company__font-medium company__mb-1">
              {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.INFO_BOX.TITLE") ||
                "Column Mapping"}
            </p>
            <p>
              {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.INFO_BOX.DESCRIPTION") ||
                "Select which columns in your CSV file correspond to the organization fields."}
            </p>
          </div>
        </div>

        <div className="company__space-y-4">
          <h3 className="company__text-lg company__font-semibold company__text-slate-900">
            {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.TITLE") ||
              "Field Mapping"}
          </h3>

          <div className="company__grid company__grid-cols-1 company__md-grid-cols-2 company__lg-grid-cols-4 company__gap-4">
            <div className="company__bg-slate-50 company__rounded-lg company__border-2 company__border-slate-200 company__p-4">
              <label className="company__block company__text-sm company__font-medium company__text-slate-900 company__mb-2">
                {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.NAME.LABEL") ||
                  "Name"}{" "}
                <span className="company__text-red-600">*</span>
              </label>
              <select
                value={mapping.name !== null ? mapping.name : ""}
                onChange={(e) =>
                  handleMappingChange(
                    "name",
                    e.target.value === "" ? null : parseInt(e.target.value),
                  )
                }
                className="company__w-full company__px-3 company__py-2 company__border company__border-slate-300 company__rounded-lg company__focus-ring-2 company__focus-ring-blue-500 company__focus-border-transparent"
              >
                <option value="">
                  {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.PLACEHOLDER") ||
                    "Select column..."}
                </option>
                {headers.map((header, index) => (
                  <option key={index} value={index}>
                    {header || `Column ${index + 1}`}
                  </option>
                ))}
              </select>
              {mapping.name !== null && (
                <p className="company__text-xs company__text-green-600 company__mt-1 company__flex company__items-center company__gap-2">
                  <MdCheckCircle className="company__w-3 company__h-3" />
                  {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.VALID_NAMES", {
                    count: validNames,
                  }) || `${validNames} valid organizations`}
                </p>
              )}
            </div>

            <div className="company__bg-slate-50 company__rounded-lg company__border-2 company__border-slate-200 company__p-4">
              <label className="company__block company__text-sm company__font-medium company__text-slate-900 company__mb-2">
                {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.ZIPCODE.LABEL") ||
                  "Zip Code"}{" "}
                <span className="company__text-red-600">*</span>
              </label>
              <select
                value={mapping.zipCode !== null ? mapping.zipCode : ""}
                onChange={(e) =>
                  handleMappingChange(
                    "zipCode",
                    e.target.value === "" ? null : parseInt(e.target.value),
                  )
                }
                className="company__w-full company__px-3 company__py-2 company__border company__border-slate-300 company__rounded-lg company__focus-ring-2 company__focus-ring-blue-500 company__focus-border-transparent"
              >
                <option value="">
                  {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.PLACEHOLDER") ||
                    "Select column..."}
                </option>
                {headers.map((header, index) => (
                  <option key={index} value={index}>
                    {header || `Column ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="company__bg-slate-50 company__rounded-lg company__border-2 company__border-slate-200 company__p-4">
              <label className="company__block company__text-sm company__font-medium company__text-slate-900 company__mb-2">
                {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.CITY.LABEL") ||
                  "City / Department"}{" "}
                <span className="company__text-slate-400">
                  {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.OPTIONAL") ||
                    "(Optional)"}
                </span>
              </label>
              <select
                value={mapping.city !== null ? mapping.city : ""}
                onChange={(e) =>
                  handleMappingChange(
                    "city",
                    e.target.value === "" ? null : parseInt(e.target.value),
                  )
                }
                className="company__w-full company__px-3 company__py-2 company__border company__border-slate-300 company__rounded-lg company__focus-ring-2 company__focus-ring-blue-500 company__focus-border-transparent"
              >
                <option value="">
                  {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.PLACEHOLDER") ||
                    "Select column..."}
                </option>
                {headers.map((header, index) => (
                  <option key={index} value={index}>
                    {header || `Column ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="company__bg-slate-50 company__rounded-lg company__border-2 company__border-slate-200 company__p-4">
              <label className="company__block company__text-sm company__font-medium company__text-slate-900 company__mb-2">
                {t(
                  "CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.DESCRIPTION.LABEL",
                ) || "Description"}{" "}
                <span className="company__text-slate-400">
                  {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.OPTIONAL") ||
                    "(Optional)"}
                </span>
              </label>
              <select
                value={mapping.description !== null ? mapping.description : ""}
                onChange={(e) =>
                  handleMappingChange(
                    "description",
                    e.target.value === "" ? null : parseInt(e.target.value),
                  )
                }
                className="company__w-full company__px-3 company__py-2 company__border company__border-slate-300 company__rounded-lg company__focus-ring-2 company__focus-ring-blue-500 company__focus-border-transparent"
              >
                <option value="">
                  {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.PLACEHOLDER") ||
                    "Select column..."}
                </option>
                {headers.map((header, index) => (
                  <option key={index} value={index}>
                    {header || `Column ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="company__space-y-4">
          <div className="company__flex company__items-center company__justify-between">
            <h3 className="company__text-lg company__font-semibold company__text-slate-900">
              {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.PREVIEW.TITLE") ||
                "Preview"}
            </h3>
            <span className="company__text-sm company__text-slate-600">
              {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.PREVIEW.SUBTITLE") ||
                "First 5 rows"}
            </span>
          </div>

          <div className="company__border company__border-slate-200 company__rounded-lg company__overflow-hidden">
            <div className="company__overflow-x-auto">
              <table className="company__w-full">
                <thead className="company__bg-slate-100">
                  <tr>
                    <th className="company__px-3 company__py-2 company__text-left company__text-xs company__font-medium company__text-slate-600 company__uppercase">
                      #
                    </th>
                    {headers.map((header, index) => {
                      let badge = null;
                      if (mapping.name === index) {
                        badge = (
                          <span className="company__ml-2 company__px-2 company__py-1 company__bg-blue-600 company__text-white company__text-xs company__rounded">
                            {t(
                              "CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.NAME.LABEL",
                            ) || "Name"}
                          </span>
                        );
                      } else if (mapping.zipCode === index) {
                        badge = (
                          <span className="company__ml-2 company__px-2 company__py-1 company__bg-green-600 company__text-white company__text-xs company__rounded">
                            {t(
                              "CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.ZIPCODE.LABEL",
                            ) || "Zip Code"}
                          </span>
                        );
                      } else if (mapping.city === index) {
                        badge = (
                          <span className="company__ml-2 company__px-2 company__py-1 company__bg-purple-600 company__text-white company__text-xs company__rounded">
                            {t(
                              "CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.CITY.LABEL",
                            ) || "City"}
                          </span>
                        );
                      } else if (mapping.description === index) {
                        badge = (
                          <span className="company__ml-2 company__px-2 company__py-1 company__bg-orange-600 company__text-white company__text-xs company__rounded">
                            {t(
                              "CONTRACT_SECTION.CSV_MAPPING_MODAL.FIELDS.DESCRIPTION.LABEL",
                            ) || "Description"}
                          </span>
                        );
                      }

                      return (
                        <th
                          key={index}
                          className={`company__px-3 company__py-2 company__text-left company__text-xs company__font-medium company__uppercase ${
                            mapping.name === index ||
                            mapping.zipCode === index ||
                            mapping.city === index ||
                            mapping.description === index
                              ? "company__bg-slate-200 company__text-slate-900"
                              : "company__text-slate-600"
                          }`}
                        >
                          <div className="company__flex company__items-center">
                            {header || `Column ${index + 1}`}
                            {badge}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="company__divide-y company__divide-slate-200 company__bg-white">
                  {previewData.map((row, rowIndex) => (
                    <tr key={rowIndex} className="company__hover-bg-slate-50">
                      <td className="company__px-3 company__py-2 company__text-xs company__text-slate-500">
                        {rowIndex + 1}
                      </td>
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className={`company__px-3 company__py-2 company__text-sm ${
                            mapping.name === cellIndex ||
                            mapping.zipCode === cellIndex ||
                            mapping.city === cellIndex ||
                            mapping.description === cellIndex
                              ? "company__bg-slate-50 company__font-medium company__text-slate-900"
                              : "company__text-slate-700"
                          }`}
                        >
                          {cell || "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {previewData.length < totalRows && (
            <p className="company__text-xs company__text-slate-500 company__text-center">
              {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.PREVIEW.MORE_ROWS", {
                count: totalRows - previewData.length,
              }) || `${totalRows - previewData.length} more rows...`}
            </p>
          )}
        </div>

        {!canImport && (
          <div className="company__bg-amber-50 company__border company__border-amber-200 company__rounded-lg company__p-4">
            <div className="company__flex company__gap-3">
              <MdErrorOutline
                className="company__w-5 company__h-5 company__text-amber-600 company__flex-shrink-0"
                style={{ marginTop: "0.125rem" }}
              />
              <div className="company__text-sm company__text-amber-900">
                <p className="company__font-medium">
                  {t(
                    "CONTRACT_SECTION.CSV_MAPPING_MODAL.STATES.MISSING_FIELDS.TITLE",
                  ) || "Required fields missing"}
                </p>
                <p>
                  {t(
                    "CONTRACT_SECTION.CSV_MAPPING_MODAL.STATES.MISSING_FIELDS.DESCRIPTION",
                  ) ||
                    "Please select columns for both organization name and zip code."}
                </p>
              </div>
            </div>
          </div>
        )}

        {canImport && (
          <div className="company__bg-green-50 company__border company__border-green-200 company__rounded-lg company__p-4">
            <div className="company__flex company__gap-3">
              <MdCheckCircle
                className="company__w-5 company__h-5 company__text-green-600 company__flex-shrink-0"
                style={{ marginTop: "0.125rem" }}
              />
              <div className="company__text-sm company__text-green-900">
                <p className="company__font-medium">
                  {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.STATES.READY.TITLE") ||
                    "Ready to import"}
                </p>
                <p>
                  {t(
                    "CONTRACT_SECTION.CSV_MAPPING_MODAL.STATES.READY.DESCRIPTION",
                    { count: validNames },
                  ) || `${validNames} organizations will be imported.`}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="company__px-6 company__py-4 company__border-t company__border-slate-200 company__bg-slate-50 company__flex company__items-center company__justify-between company__mt-6">
        <Button onClick={onClose} type="secondary">
          {t("CONTRACT_SECTION.CSV_MAPPING_MODAL.ACTIONS.CANCEL") || "Cancel"}
        </Button>
        <Button
          onClick={handleImport}
          disabled={!canImport || importing}
          type="success"
        >
          <MdUpload className="company__w-4 company__h-4" />
          {importing
            ? t("CONTRACT_SECTION.CSV_MAPPING_MODAL.ACTIONS.IMPORT_LOADING") ||
              "Importing..."
            : t("CONTRACT_SECTION.CSV_MAPPING_MODAL.ACTIONS.IMPORT", {
                count: validNames,
              }) || `Import ${validNames} organizations`}
        </Button>
      </div>
    </Modal>
  );
}
